import { useState, useMemo } from 'react';
import clsx from 'clsx';

/**
 * Lightweight multi-select with searchable list and chip preview.
 * Value is an array of ids, options are { value, label, hint? }.
 */
export function MultiSelect({ label, options = [], value = [], onChange, placeholder = 'Seleziona...', error, hint, max }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const selectedMap = useMemo(() => new Set(value.map(String)), [value]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
  }, [options, query]);

  const toggle = (val) => {
    const id = String(val);
    let next;
    if (selectedMap.has(id)) {
      next = value.filter((v) => String(v) !== id);
    } else {
      if (max && value.length >= max) return;
      next = [...value, val];
    }
    onChange(next);
  };

  const remove = (val) => onChange(value.filter((v) => String(v) !== String(val)));

  const selectedLabels = options.filter((o) => selectedMap.has(String(o.value)));

  return (
    <div className="flex flex-col gap-1.5">
      {label && <span className="text-sm font-medium text-ink">{label}</span>}
      <div
        className={clsx(
          'rounded-md border bg-surface-elevated',
          error ? 'border-danger' : 'border-border'
        )}
      >
        <div className="flex flex-wrap items-center gap-1.5 px-2 py-1.5">
          {selectedLabels.length === 0 && (
            <span className="px-1 text-sm text-ink-faint">{placeholder}</span>
          )}
          {selectedLabels.map((o) => (
            <span key={String(o.value)} className="inline-flex items-center gap-1 rounded-md bg-border/40 px-2 py-0.5 text-xs">
              {o.label}
              <button type="button" onClick={() => remove(o.value)} className="text-ink-muted hover:text-danger" aria-label="Rimuovi">×</button>
            </span>
          ))}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="ml-auto text-xs text-ink-muted hover:text-ink"
          >
            {open ? 'Chiudi' : 'Aggiungi'}
          </button>
        </div>
        {open && (
          <div className="border-t border-border p-2">
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca..."
              className="mb-2 h-8 w-full rounded-md border border-border bg-surface px-2 text-xs focus:outline-none"
            />
            <ul className="max-h-48 overflow-y-auto">
              {filtered.length === 0 ? (
                <li className="px-2 py-1.5 text-xs text-ink-muted">Nessun risultato</li>
              ) : filtered.map((o) => (
                <li key={String(o.value)}>
                  <label className="flex items-start gap-2 rounded px-2 py-1.5 text-xs hover:bg-surface">
                    <input
                      type="checkbox"
                      checked={selectedMap.has(String(o.value))}
                      onChange={() => toggle(o.value)}
                      className="mt-0.5"
                    />
                    <span>
                      <span className="text-ink">{o.label}</span>
                      {o.hint && <span className="block text-[11px] text-ink-muted">{o.hint}</span>}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      {error ? <span className="text-xs text-danger">{error}</span> : hint ? <span className="text-xs text-ink-muted">{hint}</span> : null}
    </div>
  );
}
